import React, { useRef } from 'react';
import type { MentionTextareaHandle } from 'src/components/Form/mention-textarea';
import { array, boolean, object, string } from 'yup';
import { extractHookFormErrors } from 'src/components/Form/Form.utils';
import { useRequestRevisionsStore } from 'src/features/revisions/use-revisions-context-provider';
import { useVideoProgressActions } from 'src/features/video-player/use-video-progress-store';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRequestRevisionVideoPlayerGlobalStore } from 'src/features/revisions/use-request-revision-video-player-global-store';
import { shallow } from 'zustand/shallow';
import { yupResolver } from '@hookform/resolvers/yup';
import EmojiPicker from 'src/components/ui/emoji-picker';
import { Button } from 'src/components/ui/button';
import { Smile, UploadCloud } from 'lucide-react';
import { RevisionChatSelectedMedia } from 'src/pages/RevisionsPage/revisions-content-main/revision-chat-selected-media';
import { DropzonePopoverInput } from 'src/components/Form/S3UploadInput/hook-form/dropzone-popover-input';
import { ElapsedTimeBadge } from './elapsed-time-badge';
import { useMediaUploadStore } from 'src/lib/services/media-upload-store';
import { useShallow } from 'zustand/react/shallow';
import { StoreCommentFormObject } from 'src/pages/RevisionsPage/revisions-content-main/revision-chat-content.types';
import { ButtonLoader } from 'src/components/ui/loader';
import { useBreakpoint } from 'src/lib/hooks';
import { MentionTextarea } from 'src/components/Form/mention-textarea';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';

type Props = {
  className?: string;
  children?: React.ReactNode;
  commentMutation: any;
  onSubmit?: () => void;
  replyCommentId?: string;
};

const validationSchema = object().shape({
  include_time: boolean(),
  media: array().nullable(),
  comment: string()
    .nullable()
    .when('media', {
      is: (media: any) => {
        return !media.length;
      },
      then: string().required('Field is required'),
    }),
});

const RevisionChatInputForm: React.FC<Props> = ({
  className,
  replyCommentId,
  commentMutation,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();

  const { selectedRevision, selectedRevisionMedia } = useRequestRevisionsStore();
  // Both this form and revision-chat-edit-comment-form expect a
  // DecoratedRequestProvider higher in the tree (RevisionsPage wraps in one).
  // Any future mount site — modal preview, etc. — needs the same wrapper.
  const request = useDecoratedRequestContext();
  const mentionRef = useRef<MentionTextareaHandle | null>(null);

  const { status } = useMediaUploadStore(useShallow(({ status }) => ({ status })));

  const { isLatest = false } = selectedRevision ?? {};

  const defaultValues = {
    comment: '',
    include_time: !!(selectedRevisionMedia?.isVideo || selectedRevisionMedia?.isAudio) && isLatest,
    media: [],
  };

  // :)
  const { getPlayedSeconds } = useVideoProgressActions();

  const pageStore = useRequestRevisionVideoPlayerGlobalStore(
    ({ timeFormat, pause }) => ({
      timeFormat,
      pause,
    }),
    shallow,
  );

  const form = useForm<StoreCommentFormObject>({
    defaultValues,
    /* eslint-disable */
    //@ts-ignore
    resolver: yupResolver(validationSchema),
  });

  const includeTime = form.watch('include_time');
  const media = form.watch('media');

  const mediaUploading = (media ?? []).some((m) => status[m.id]?.isUploading);
  const uploadsFailed = (media ?? []).some((m) => status[m.id]?.failed);

  const submit = async (values: StoreCommentFormObject) => {
    commentMutation
      .mutateAsync([
        replyCommentId ? replyCommentId : selectedRevision!.id,
        {
          ...values,
          revision_timestamp:
            !!(selectedRevisionMedia?.isVideo || selectedRevisionMedia?.isAudio) &&
            isLatest &&
            values.include_time
              ? getPlayedSeconds()
              : undefined,
          media_id: selectedRevisionMedia?.id,
        },
      ])
      .catch((e: any) => {
        const errors = extractHookFormErrors(e) ?? {};

        console.error(errors);
        Object.keys(errors).forEach((key) => {
          form.setError(key as any, { type: 'custom', message: errors[key] });
        });
      })
      .finally(() => {
        form.reset();
        onSubmit?.();
      });
  };

  const handleEmojiSelect = (emoji: any) => {
    // Delegate to MentionTextarea so the caret-to-markup translation stays
    // in one place. Direct slicing of `comment` at textarea.selectionStart
    // would shred any preceding `@[Name](uuid)` token because the caret is
    // in plain-text coordinates while `comment` is the markup string.
    mentionRef.current?.insertAtCaret(emoji.native);
  };

  return (
    <div className={className}>
      <FormProvider {...form}>
        <RevisionChatSelectedMedia name={'media'} />
        <form onSubmit={form.handleSubmit(submit)}>
          <div>
            <Controller
              name={'comment'}
              control={form.control}
              render={({ field }) => (
                <MentionTextarea
                  ref={mentionRef}
                  requestId={request.id}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  rows={isXs ? 1 : 2}
                  placeholder={t('Type a message...')!}
                  onKeyDown={(e) => {
                    if (includeTime) {
                      pageStore.pause();
                    }

                    // react-mentions intercepts Enter/Up/Down/Escape when the
                    // suggestion list is open. Bail out so picking a mention
                    // with Enter doesn't also submit the form.
                    if (e.defaultPrevented) {
                      return;
                    }

                    if (e.shiftKey || e.code !== 'Enter') {
                      return;
                    }

                    if (
                      form.formState.isSubmitting ||
                      mediaUploading ||
                      uploadsFailed ||
                      commentMutation.isLoading
                    ) {
                      return;
                    }

                    e.preventDefault();

                    form.handleSubmit(submit)();
                  }}
                />
              )}
            />
          </div>
          <div className={'tw-mt-2 tw-flex tw-items-center tw-gap-2'}>
            <DropzonePopoverInput name={'media'} model={'request_revision_comment'}>
              <Button type={'button'} variant={'outline'} size={'sm'}>
                <UploadCloud size={18} />
              </Button>
            </DropzonePopoverInput>
            <EmojiPicker onSelect={handleEmojiSelect}>
              <Button type={'button'} variant={'outline'} size={'sm'}>
                <Smile size={18} />
              </Button>
            </EmojiPicker>

            <ElapsedTimeBadge
              title={t('Check the box to include current playback time in the message!')!}
              disabled={!(selectedRevisionMedia?.isVideo || selectedRevisionMedia?.isAudio)}
              formattingOptions={{
                format: pageStore.timeFormat,
                fps: selectedRevisionMedia?.fps ?? 24,
              }}
            />

            <Button
              variant={'brand'}
              className={'tw-ml-auto'}
              size={'sm'}
              disabled={
                form.formState.isSubmitting ||
                mediaUploading ||
                uploadsFailed ||
                commentMutation.isLoading
              }
              type={'submit'}
            >
              {!form.formState.isSubmitting && !commentMutation.isLoading && t('Send')}
              <ButtonLoader
                className={'tw-ms-0'}
                loading={form.formState.isSubmitting || commentMutation.isLoading}
              />
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export { RevisionChatInputForm };
